import React from 'react';

import { Row, Col } from '../../../../grid';
import StatusIndicator from '../../status/status-indicator';
import { LoadingBlock } from '../../loading/progress';
import { AppSummaryInstanceProps, SummaryCell } from '../index';
import { useGetSummary } from '../../../../libs/summary';
import { IEpxDiabetesSettings } from '../../../../libs/apps/epxdiabetes';
import { TriageCategory } from '../../../../contexts/triage/types';

interface SummaryEntry {
  min?: number;
  max?: number;
  avg?: number;
  stddev?: number;
}

export interface EpxDiabetesSummaryResponse {
  summary: {
    preprandial?: SummaryEntry;
    postprandial?: SummaryEntry;
  };
  settings: IEpxDiabetesSettings;
}

const StdDevStatus = <span>{'\u00B1 '}</span>;

const getStatus = (
  thresholds: IEpxDiabetesSettings['thresholds'],
  value: number,
  key: 'min' | 'max' | 'avg'
): JSX.Element => {
  let level = TriageCategory.Success;
  if (key === 'min') {
    if (value <= thresholds.red.fasting.min) {
      level = TriageCategory.Danger;
    } else if (value >= thresholds.red.fasting.avg) {
      level = TriageCategory.Danger;
    } else if (value >= thresholds.yellow.fasting.avg) {
      level = TriageCategory.Warning;
    }
  } else if (key === 'max') {
    if (value >= thresholds.red.fasting.max) {
      level = TriageCategory.Danger;
    } else if (value >= thresholds.red.fasting.avg) {
      level = TriageCategory.Danger;
    } else if (value >= thresholds.yellow.fasting.avg) {
      level = TriageCategory.Warning;
    }
  } else if (key === 'avg') {
    if (value >= thresholds.red.fasting.avg) {
      level = TriageCategory.Danger;
    } else if (value >= thresholds.yellow.fasting.avg) {
      level = TriageCategory.Warning;
    }
  }
  return (
    <StatusIndicator category={level} title={false} isAltCategoryWording />
  );
};

const EpxDiabetesSummary = ({
  params
}: AppSummaryInstanceProps): JSX.Element => {
  const { data, isLoading } = useGetSummary<EpxDiabetesSummaryResponse>(
    '/epxdiabetes/data_summary/',
    params
  );
  if (!data || isLoading) {
    return <LoadingBlock />;
  }
  const { summary, settings } = data;
  const { thresholds } = settings;
  return (
    <div>
      <h4 className="mb-8">Blood Sugar Summary from Past 2 Weeks</h4>
      <Row>
        <Col sm={6}>
          <SummaryCell
            label="Pre-Meal Min."
            value={summary?.preprandial?.min}
            status={getStatus(thresholds, summary?.preprandial?.min, 'min')}
            unit="mg/dl"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Pre-Meal Max."
            value={summary?.preprandial?.max}
            status={getStatus(thresholds, summary?.preprandial?.max, 'max')}
            unit="mg/dl"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Pre-Meal Avg."
            value={summary?.preprandial?.avg}
            status={getStatus(thresholds, summary?.preprandial?.avg, 'avg')}
            unit="mg/dl"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Pre-Meal StdDev."
            value={summary?.preprandial?.stddev}
            status={StdDevStatus}
            unit="mg/dl"
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <SummaryCell
            label="Post-Meal Min."
            value={summary?.postprandial?.min}
            status={getStatus(thresholds, summary?.postprandial?.min, 'min')}
            unit="mg/dl"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Post-Meal Max."
            value={summary?.postprandial?.max}
            status={getStatus(thresholds, summary?.postprandial?.max, 'max')}
            unit="mg/dl"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Post-Meal Avg."
            value={summary?.postprandial?.avg}
            status={getStatus(thresholds, summary?.postprandial?.avg, 'avg')}
            unit="mg/dl"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Post-Meal StdDev."
            value={summary?.postprandial?.stddev}
            status={StdDevStatus}
            unit="mg/dl"
          />
        </Col>
      </Row>
    </div>
  );
};

export default EpxDiabetesSummary;
